import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { ArrowLeft, CheckCircle, Lock } from 'lucide-react';

export const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (!token) { setError('Missing reset token. Please use the link from your reset email.'); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#06120B' }}>
      <Card className="w-full max-w-md" data-testid="reset-password-card">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2"><Lock className="w-5 h-5" /> Set New Password</CardTitle>
          <CardDescription>Enter your new password below</CardDescription>
        </CardHeader>
        <CardContent>
          {!done ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="reset-error">{error}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="Min. 6 characters" data-testid="reset-password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm Password</Label>
                <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} placeholder="Repeat password" data-testid="reset-confirm" />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="reset-submit">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
              <div className="text-center text-sm text-gray-500">
                <Link to="/auth/login" className="inline-flex items-center gap-1 text-emerald-600 hover:underline">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to login
                </Link>
              </div>
            </form>
          ) : (
            <div className="space-y-4" data-testid="reset-success">
              <div className="flex flex-col items-center text-center py-4">
                <CheckCircle className="w-12 h-12 text-emerald-500 mb-3" />
                <p className="font-medium text-gray-900 mb-1">Password Reset Successfully</p>
                <p className="text-sm text-gray-500">You can now log in with your new password.</p>
              </div>
              <Button onClick={() => navigate('/auth/login')} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="reset-go-login">
                Go to Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
